// src/cloudinaryService.js
// Free file uploads via Cloudinary — no credit card required.
// IMPORTANT: The upload preset must be set to "Unsigned" in your Cloudinary dashboard.

const CLOUD_NAME = "dedrxhoh7";
const UPLOAD_PRESET = "virtual-hr-uploads";

/**
 * Upload a file or Blob to Cloudinary with live progress tracking.
 * @param {File|Blob} file       - The file to upload (PDF, DOCX, or WebM video)
 * @param {string}    folder     - Cloudinary folder path  e.g. "resumes/uid123"
 * @param {function}  onProgress - Called with a 0–100 integer as upload progresses
 * @param {string}    resourceType - "auto", "video", or "raw" (defaults to "auto")
 * @returns {Promise<string>}    - Resolves to the secure download URL
 */
export function uploadToCloudinary(file, folder, onProgress, resourceType = "auto") {
  return new Promise((resolve, reject) => {
    if (!CLOUD_NAME || !UPLOAD_PRESET) {
      reject(
        new Error(
          "Cloudinary config missing. " +
          "Make sure REACT_APP_CLOUDINARY_CLOUD_NAME and " +
          "REACT_APP_CLOUDINARY_UPLOAD_PRESET are set in your .env file."
        )
      );
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", UPLOAD_PRESET);
    if (folder) formData.append("folder", folder);

    const xhr = new XMLHttpRequest();
    xhr.open(
      "POST",
      `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/${resourceType}/upload`
    );

    // Live upload progress
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };

    xhr.onload = () => {
      if (xhr.status === 200) {
        try {
          const res = JSON.parse(xhr.responseText);
          resolve(res.secure_url);
        } catch {
          reject(new Error("Failed to parse Cloudinary response."));
        }
      } else {
        try {
          const err = JSON.parse(xhr.responseText);
          reject(new Error(err.error?.message || `Upload failed (HTTP ${xhr.status})`));
        } catch {
          reject(new Error(`Upload failed with HTTP status ${xhr.status}`));
        }
      }
    };

    xhr.onerror = () => reject(new Error("Network error during upload. Check your internet connection."));
    xhr.onabort = () => reject(new Error("Upload was aborted."));

    xhr.send(formData);
  });
}
